import { money } from '../lib/money';
import type { Invoice } from '../types';

/**
 * The invoice book in the shape of the client's Invoice Tracker sheet.
 *
 * A second view of the same rows rather than a second page, because the
 * summary table and this one answer different questions off one list and a
 * separate page would mean a second creation form to drift apart from the
 * first.
 *
 * Rows with no placement show blanks across the middle. That is deliberate:
 * placementId is nullable because not everything billed is a placement fee,
 * and a tracker that silently dropped those rows could not be reconciled
 * against the ledger. An empty cell is a true statement; a missing row is not.
 *
 * Sr-No is rendered from position and never stored. It changes with sorting
 * and filtering, so a stored one would disagree with what is on screen.
 *
 * Status is the real seven-state value, not a paid/unpaid toggle — draft,
 * raised, partially paid, paid, cancelled, and overdue computed at read time.
 */
function Cell({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const empty = children === null || children === undefined || children === '';
  return (
    <td className={`whitespace-nowrap px-3 py-2 ${className}`}>
      {empty ? <span className="text-gray-300">—</span> : children}
    </td>
  );
}

/** The sheet's "Flat/CTC%" as one readable cell. */
function feeLabel(invoice: Invoice): string | null {
  if (invoice.feeBasis === 'flat') return 'Flat';
  if (invoice.feeBasis === 'ctc_percent') {
    return invoice.feePercent ? `${invoice.feePercent}% of CTC` : '% of CTC';
  }
  return null;
}

const STATUS_TONE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  raised: 'bg-blue-100 text-blue-700',
  partially_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-400',
  written_off: 'bg-gray-100 text-gray-400',
};

/**
 * Only the identifying column stays frozen.
 *
 * Candidate is the row anchor: it tells you whose invoice it is and remains
 * visible while the money columns drift. Keeping more than one sticky column
 * leaves fragments of adjacent cells visible between them, and the result is an
 * orphaned Mobile or Position fragment that reads like a second column broke
 * free from the row.
 */
const STICKY_H = 'sticky left-0 z-20 w-40 bg-gray-50 px-3 py-2.5 text-left font-medium';
const STICKY_C = 'sticky left-0 z-10 w-40 bg-white px-3 py-2 group-hover:bg-gray-50';

const PAYMENT_LABEL: Record<Invoice['paymentStatus'], string> = {
  unpaid: 'Not received',
  part_paid: 'Part received',
  paid: 'Received',
};

/**
 * What the payment cell should say, and whether it is a control.
 *
 * "Not received" on a draft was the problem this replaces. It is a true
 * statement about the money and a misleading one about the screen: it reads as
 * a state somebody could act on, sitting in a column where every other row can
 * be acted on, when in fact the server refuses a payment on a draft outright.
 * The cell now names the reason and the next step instead.
 *
 * Returned as data rather than branched inline so the cell renders one shape.
 * The rule it encodes is record_payment's, not a second opinion: draft and
 * cancelled are exactly the two statuses it rejects.
 */
function paymentCell(invoice: Invoice): {
  label: string;
  tone: string;
  actionable: boolean;
  hint?: string;
} {
  if (invoice.status === 'draft') {
    return {
      label: 'Raise the invoice first',
      tone: 'bg-amber-50 text-amber-800',
      actionable: false,
      hint: 'A draft is not a claim on anyone yet, so nothing can arrive against it.',
    };
  }
  if (invoice.status === 'cancelled') {
    return {
      label: 'Cancelled',
      tone: 'bg-gray-100 text-gray-400',
      actionable: false,
      hint: 'A withdrawn invoice cannot take a payment.',
    };
  }
  // Settled: still worth stating, but there is nothing left to record.
  if (invoice.paymentStatus === 'paid') {
    return { label: PAYMENT_LABEL.paid, tone: PAYMENT_TONE.paid, actionable: false };
  }
  return {
    label: PAYMENT_LABEL[invoice.paymentStatus],
    tone: PAYMENT_TONE[invoice.paymentStatus],
    actionable: true,
  };
}

const PAYMENT_TONE: Record<Invoice['paymentStatus'], string> = {
  unpaid: 'bg-gray-100 text-gray-600',
  part_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-700',
};

export default function InvoiceTrackerTable({
  invoices,
  onOpen,
  onRecordPayment,
  onEdit,
}: {
  invoices: Invoice[];
  onOpen: (invoice: Invoice) => void;
  /**
   * Omitted where the viewer may not record payments. The endpoint is company
   * admin and above, and /admin/billing also serves managers who read but do
   * not receipt — so the Payment cell stays a plain badge rather than becoming
   * a control that fails on click.
   */
  onRecordPayment?: (invoice: Invoice) => void;
  /**
   * Omitted where the viewer may not edit. Like onRecordPayment, the column is
   * not offered rather than shown and refused.
   */
  onEdit?: (invoice: Invoice) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 z-30 border-b border-gray-200 bg-gray-50 uppercase text-gray-500">
          <tr>
            <th className={STICKY_H}>Candidate</th>
            <th className="px-3 py-2.5 font-medium">#</th>
            <th className="px-3 py-2.5 font-medium">Invoice</th>
            <th className="px-3 py-2.5 font-medium">Client</th>
            <th className="px-3 py-2.5 font-medium">Dated</th>
            <th className="px-3 py-2.5 font-medium">Mobile</th>
            <th className="px-3 py-2.5 font-medium">Position</th>
            <th className="px-3 py-2.5 font-medium">D.O.J</th>
            <th className="px-3 py-2.5 font-medium">Location</th>
            <th className="px-3 py-2.5 font-medium">CTC</th>
            <th className="px-3 py-2.5 font-medium">Flat / CTC%</th>
            <th className="px-3 py-2.5 text-right font-medium">Net</th>
            <th className="px-3 py-2.5 font-medium">GST</th>
            <th className="px-3 py-2.5 text-right font-medium">With GST</th>
            <th className="px-3 py-2.5 font-medium">TDS</th>
            <th className="px-3 py-2.5 text-right font-medium">After TDS</th>
            <th className="px-3 py-2.5 text-right font-medium">Received</th>
            <th className="px-3 py-2.5 font-medium">Paid on</th>
            <th className="px-3 py-2.5 font-medium">Working</th>
            <th className="px-3 py-2.5 font-medium">Recruiter</th>
            <th className="px-3 py-2.5 font-medium">Status</th>
            <th className="px-3 py-2.5 font-medium">Payment</th>
            {onEdit && <th className="px-3 py-2.5 font-medium"></th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {invoices.length === 0 && (
            <tr>
              <td colSpan={23} className="px-3 py-8 text-center text-gray-400">
                No invoices match that.
              </td>
            </tr>
          )}
          {invoices.map((invoice, index) => (
            <tr
              key={invoice.id}
              className="group cursor-pointer hover:bg-gray-50"
              onClick={() => onOpen(invoice)}
            >
              <Cell className={`${STICKY_C} text-gray-900`}>
                {invoice.candidateName}
              </Cell>
              <Cell className="text-gray-400">{index + 1}</Cell>
              <Cell className="font-mono text-gray-900">{invoice.invoiceNumber}</Cell>
              <Cell className="text-gray-900">{invoice.clientName}</Cell>
              <Cell className="text-gray-600">{invoice.invoiceDate}</Cell>
              <Cell className="text-gray-600">{invoice.candidatePhone}</Cell>
              <Cell className="text-gray-600">{invoice.position}</Cell>
              <Cell className="text-gray-600">{invoice.dateOfJoining}</Cell>
              <Cell className="text-gray-600">{invoice.placementLocation}</Cell>
              <Cell className="tabular-nums text-gray-600">
                {invoice.ctcAnnual ? money(invoice.ctcAnnual) : null}
              </Cell>
              <Cell className="text-gray-600">{feeLabel(invoice)}</Cell>
              <Cell className="text-right tabular-nums text-gray-900">
                {money(invoice.amount)}
              </Cell>
              {/* The rate, not a recomputation — billing_tax works the figures
                  and this shows what it was given. */}
              <Cell className="text-gray-500">
                {invoice.gstRate ? `${invoice.gstRate}%` : null}
              </Cell>
              <Cell className="text-right tabular-nums text-gray-900">
                {money(invoice.totalAmount)}
              </Cell>
              <Cell className="text-gray-500">
                {invoice.tdsRate
                  ? `${invoice.tdsRate}%${invoice.tdsSection ? ` ${invoice.tdsSection}` : ''}`
                  : null}
              </Cell>
              <Cell className="text-right tabular-nums text-gray-600">
                {invoice.expectedReceipt ? money(invoice.expectedReceipt) : null}
              </Cell>
              <Cell className="text-right tabular-nums text-gray-900">
                {money(invoice.amountReceived)}
              </Cell>
              <Cell className="text-gray-600">{invoice.lastPaymentOn}</Cell>
              <Cell className="text-gray-600">
                {invoice.placementStatus?.replace(/_/g, ' ')}
              </Cell>
              <Cell className="text-gray-600">{invoice.recruiterName}</Cell>
              <Cell>
                {/* The real status. Overdue is computed at read time and is
                    not a state the invoice sits in, so it is shown beside the
                    status rather than instead of it. */}
                <span
                  className={`rounded px-2 py-0.5 ${
                    STATUS_TONE[invoice.status] ?? 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {invoice.status.replace(/_/g, ' ')}
                </span>
                {invoice.isOverdue && (
                  <span className="ml-1 rounded bg-red-100 px-2 py-0.5 text-red-700">
                    overdue
                  </span>
                )}
              </Cell>
              {/* The money, beside the document's state rather than instead of
                  it. A cancelled invoice that was never paid is both, and one
                  column cannot say both. Derived — never set by hand.

                  This is also the control now. It used to be a badge with a
                  separate Record button next to it, which meant the column
                  saying "Not received" was not the column you could do
                  anything about. One cell states the position and offers the
                  only action there is. */}
              <Cell>
                {(() => {
                  const cell = paymentCell(invoice);
                  if (!onRecordPayment || !cell.actionable) {
                    return (
                      <span
                        className={`rounded px-2 py-0.5 ${cell.tone}`}
                        title={cell.hint}
                      >
                        {cell.label}
                      </span>
                    );
                  }
                  return (
                    <button
                      type="button"
                      className={`rounded px-2 py-0.5 underline decoration-dotted underline-offset-2 hover:brightness-95 ${cell.tone}`}
                      title="Record what has arrived"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRecordPayment(invoice);
                      }}
                    >
                      {cell.label} · Record
                    </button>
                  );
                })()}
              </Cell>
              {onEdit && (
                <Cell>
                  {/* A draft is a document still being assembled. Once raised,
                      the client holds a copy — changing the figures silently
                      would leave two versions of one invoice number
                      disagreeing about money.

                      The reason replaces the button rather than greying it,
                      because a disabled control with no explanation is a
                      question nobody can answer from the screen. The server
                      refuses it either way. */}
                  {invoice.status === 'draft' ? (
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(invoice);
                      }}
                    >
                      Edit
                    </button>
                  ) : invoice.paymentStatus === 'unpaid'
                    && invoice.status !== 'cancelled' ? (
                    <span className="text-xs text-gray-500">
                      Sent — cancel to correct
                    </span>
                  ) : invoice.status === 'cancelled' ? (
                    <span className="text-xs text-gray-400">Cancelled</span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      Paid against — cannot be changed
                    </span>
                  )}
                </Cell>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
